import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ScreenTitle from '../../common/components/screenTitle/ScreenTitle';
import styles from './ListChargesScreen.module.scss';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, FUELS, LOGGER_LOG_TYPE, SEGMENT_FEATURES } from 'Config';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import GaugeIcon from 'assets/svg/gauge.svg';
import ClockIcon from 'assets/svg/desktop_i_clock.svg';
import { useHistory, useParams } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import ChargesService from 'api/charges/ChargesService';
import { ChargeCounterListDto, ChargeDto } from 'api/charges/models/ChargeDto';
import { ChargesSearchCriteria } from 'api/charges/models/ChargesSearchCriteria';
import Loading from 'common/services/Loading';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { useDebouncedCallback } from 'use-debounce';
import RenderSVG from '../../common/components/renderSVG/RenderSVG';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/popover/PopoverItem';
import { FaEllipsisH } from 'react-icons/fa';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Popover from 'common/components/popover/Popover';
import ChargeFiltersScreen, { Filters } from './components/ChargeFiltersScreen';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import IconWithText from 'common/components/iconWithText/IconWithText';
import ReactTooltip from 'react-tooltip';
import { ChargesStatusColorEnum } from 'api/charges/enums/ChargesStatusColorEnum';
import CarIcon from 'assets/svg/car.svg';
import CoinsIcon from 'assets/svg/coins.svg';
import MediaIcon from 'assets/svg/media.svg';
import ImportIcon from 'assets/svg/file-arrow-up-solid.svg';
import DeletedVehicleCircle from 'common/components/deletedVehicleCircle/DeletedVehicleCircle';
import { SituationType } from 'screens/vehicle/components/carChargeTab/components/ChargeScreen';
import { GenericType } from 'api/genericTypes/enums/GenericType';
import VehiclesService from 'api/vehicles/VehiclesService';
import InputSearch from 'common/components/inputSearch/InputSearch';
import AiIcon from 'assets/svg/icon_centro_custos_ai.svg';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import GeofencesService from 'api/geofences/GeofencesService';
import { ReactComponent as CostCenterCompleteIcon } from 'assets/svg/icon_centro_custos_completo.svg';
import { ReactComponent as CostCenterIncompleteIcon } from 'assets/svg/icon_centro_custos_incompleto.svg';
import { ReactComponent as CostCenterEmptyIcon } from 'assets/svg/icon_centro_custos_zero.svg';
import SimpleToggleSwitch from 'common/components/simpleToggleSwitch/SimpleToggleSwitch';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import InfoCard from 'common/components/infoCard/InfoCard';
import VatNotice from 'common/components/vatNotice/VatNotice';
import colors from 'styles/export/colors.module.scss';


export type Props = {
    isVehicle?: boolean;
    isDriver?: boolean;
    editable: boolean;
    onClickItem: (item: ChargeDto, isDetails: boolean) => void;
    createNewCharge?: () => void;
    criteriaProp?: ChargesSearchCriteria;
    onDeleteFromList?: () => void;
    tourReRender?: () => void;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
];

const ListChargesScreen = ({ onClickItem, createNewCharge, isVehicle, isDriver, criteriaProp, onDeleteFromList, editable, tourReRender, ...props }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasImportChargesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IMPORT_EXPENSES_WRITE']);
    const hasChargesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_CHARGES_WRITE']);
    const hasReadCostCenterPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COST_CENTER_READ']);
    const [geofencesOptions, setGeofencesOptions] = useState<SelectValueLabel[]>([]);
    const imageCacheKey = useRef(new Date().getTime());
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const { driverId, vehicleId, month } = useParams<{ driverId: string, vehicleId: string, month: string }>();

    const [charges, setCharges] = useState<ChargeDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [counters, setCounters] = useState<ChargeCounterListDto>();

    const [criteria, setCriteria] = useState<ChargesSearchCriteria>(!criteriaProp ? {
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        vehicleId: isVehicle ? vehicleId : undefined,
        responsibleId: isDriver ? driverId : undefined,
        orderColumn: 'c.date',
        orderBy: 'desc',
        startDate: month ? moment(month).startOf('month').toDate() : undefined,
        endDate: month ? moment(month).endOf('month').toDate() : undefined,
        vehiclesInactive: true,
        translations: t('common.generic_types', { returnObjects: true }),
        fuelTypeTranslations: t('vehicle.fuels', { returnObjects: true })
    } : { ...criteriaProp, fuelTypeTranslations: t('vehicle.fuels', { returnObjects: true }), translations: t('common.generic_types', { returnObjects: true }) });

    const [filters, setFilters] = useState<Filters>(month ? { startDate: criteria.startDate, endDate: criteria.endDate, vehiclesInactive: true } : { vehiclesInactive: true });
    const [filtersTotal, setFiltersTotal] = useState(month ? 1 : 0);
    const [vehicleSegmentFeatures, setVehicleSegmentFeatures] = useState<string[]>([])
    const [aiSelected, setAiSelected] = useState<ChargeDto[]>([])
    const [showAiSelected, setShowAiSelected] = useState<boolean>(false)

    const [reRenderSearch, setReRenderSearch] = useState<number>(0);

    const [itemToRemove, setItemToRemove] = useState<ChargeDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);

    const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);

    const isFirstLoad = useRef(true);
    const skipCounters = useRef(false);
    const isLoadingData = useRef(false);
    const filterJsonKey = 'CHARGESFILTER-'.concat(isVehicle ? 'v-'.concat(vehicleId) : isDriver ? 'd-'.concat(driverId) : '0');

    // Order state for DataTableWithSidebar
    const [order, setOrder] = useState<OrderData<ChargeDto>>({
        field: 'date' as keyof ChargeDto,
        orderColumn: 'c.date',
        isOrderAsc: false,
    });

    const getFieldFromOrderColumn = (oc: string): keyof ChargeDto => {
        const mapping: Record<string, string> = {
            'vehicle.registration_number': 'vehicleRegistrationNumber',
            'vb.name': 'vehicleBrandName',
            'vm.name': 'vehicleModelName',
            'responsible.real_name': 'responsibleName',
            'c.date': 'date',
            'translated_type_name': 'typeName',
            'c.kilometers': 'kilometers',
            'c.hours': 'hours',
            'supplier.name': 'supplierName',
            'c.cost': 'cost',
            'c.payment_date': 'paymentDate',
            'geof.geofences_percentage': 'geofencesPercentage',
            'km_since_last_fueling': 'kmSinceLastFueling',
            'price_per_liter': 'pricePerLiter',
        };
        return (mapping[oc] ?? 'date') as keyof ChargeDto;
    };

    // Bidirectional scroll hook
    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = useBidirectionalScroll({
        cacheKey: filterJsonKey,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const isStandaloneView = !isVehicle && !isDriver;

    const getData = async (isLoadMore = false) => {
        if (isLoadingData.current) return;
        isLoadingData.current = true;

        // Capture whether this call should fetch counters. Doing it once up-front
        // avoids a race where a filter change mid-flight flips skipCounters=false
        // but the next getData sees skipCounters=true again (reset by this finally).
        const shouldFetchCounters = !skipCounters.current;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            let countersDB = null;
            if (shouldFetchCounters) {
                countersDB = await ChargesService.getCountersList(criteria);
            }

            saveOrdernationFilters();

            const [chargesResult, _vehicleSegmentFeatures] = await Promise.all([
                ChargesService.getList(criteria),
                criteria.vehicleId ? VehiclesService.getVehicleSegmentFeatures(criteria.vehicleId) : null
            ]);

            if (isLoadMore) {
                setCharges(prev => [...prev, ...chargesResult.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setCharges(chargesResult.items);
            }
            setTotalItems(chargesResult.totalItems);

            if (isSelectAllChecked) {
                if (isLoadMore) {
                    setAiSelected(prev => [...prev, ...chargesResult.items]);
                } else {
                    setAiSelected(chargesResult.items);
                }
            }

            setVehicleSegmentFeatures(_vehicleSegmentFeatures ?? [])
            if (countersDB) {
                setCounters(countersDB);
            }

            // Handle scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(chargesResult.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await ChargesService.getList(prevCriteria);
                        setCharges(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await ChargesService.getList(nextCriteria);
                        setCharges(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get charges list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            // Only suppress counter refetch if THIS call actually fetched them;
            // otherwise a concurrent filter change (which set skipCounters=false)
            // would be overwritten and the next fetch would skip counters.
            if (shouldFetchCounters) {
                skipCounters.current = true;
            }
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
            tourReRender && tourReRender();
        }
    };

    // Infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || charges.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, charges.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: ChargesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await ChargesService.getList(prevCriteria);

            setCharges(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get previous charges page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [charges, isLoadingPrevious, adjustScrollAfterPrepend]);

    // Sync AI selection with charges
    useEffect(() => {
        if (isSelectAllChecked && charges.length > 0) {
            setAiSelected(charges);
        }
    }, [charges, isSelectAllChecked]);

    const showRemoveItemDialog = (item: ChargeDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const showField = (feature: string) => {
        return (vehicleSegmentFeatures.findIndex(x => x == feature) ?? -1) >= 0;
    }

    const renderTableActionCell = (
        row: ChargeDto,
    ) => {
        const urlDetails = (isVehicle || isDriver) ? `/${isDriver ? 'drivers' : 'vehicles'}/details/${isDriver ? driverId : vehicleId}/costs/details/${row.id}` : `charges/details/${row.id}`;
        const urlEdit = (isVehicle || isDriver) ? `/${isDriver ? 'drivers' : 'vehicles'}/details/${isDriver ? driverId : vehicleId}/costs/edit/${row.id}` : `charges/edit/${row.id}`;

        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        { hasChargesWritePolicy && !(row.vehicleId && !row.vehicleActive)  && <DropdownItem url={urlEdit}>
                            {t('common.edit')}
                        </DropdownItem>}
                        { hasChargesWritePolicy && !(row.vehicleId && !row.vehicleActive)  && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const onSelectAll = (value: boolean) => {
        setIsSelectAllChecked(value);

        if (value) {
            setAiSelected(charges);
        } else {
            setAiSelected([]);
        }
    };

    // Order handler for DataTableWithSidebar
    const handleOrderChange = (orderData: OrderData<ChargeDto>) => {
        setOrder(orderData);
        setCharges([]);
        resetScrollState();
        setTotalItems(0);
        skipCounters.current = true;
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    // DataTableWithSidebar columns (shared between standalone and tab views)
    const dataTableColumns: DataTableColumn<ChargeDto>[] = useMemo(() => {
        const isTabView = isVehicle || isDriver;

        const vehicleColumns: DataTableColumn<ChargeDto>[] = !isTabView ? [
            {
                field: 'vehiclePhotoUrl' as keyof ChargeDto,
                name: t('common.image') as string,
                hideOrderButton: true,
                width: 80,
                hideOn: ['sm', 'md'],
                excludeFromExport: true,
                renderCell: row => (
                    <div className={styles.imageDescCell}>
                        <div className={styles.contentImage}>
                            <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                        </div>
                    </div>
                ),
            },
            {
                field: 'vehicleRegistrationNumber' as keyof ChargeDto,
                name: t('charges.list.vehicle') as string,
                orderColumn: 'vehicle.registration_number',
                width: 150,
                renderCell: row => (
                    <div className={styles.descCell}>
                        <div className={styles.columnPlate}>
                            {row.vehicleRegistrationNumber}
                            {!row.vehicleActive && <DeletedVehicleCircle id={row.id ?? ''} />}
                        </div>
                    </div>
                ),
            },
            {
                field: 'vehicleBrandName' as keyof ChargeDto,
                name: t('vehicle.brand') as string,
                orderColumn: 'vb.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleBrandName || '-'}</>,
            },
            {
                field: 'vehicleModelName' as keyof ChargeDto,
                name: t('vehicle.model') as string,
                orderColumn: 'vm.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleModelName || '-'}</>,
            },
            {
                field: 'vehicleSegmentName' as keyof ChargeDto,
                name: t('vehicle.type') as string,
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
            },
            {
                field: 'vehicleLocalNames' as keyof ChargeDto,
                name: t('vehicle.local') as string,
                hideOrderButton: true,
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleLocalNames || '-'}</>,
            },
        ] : [];

        const kmColumns: DataTableColumn<ChargeDto>[] = (!isTabView || showField(SEGMENT_FEATURES.KM)) ? [
            {
                field: 'kilometers' as keyof ChargeDto,
                name: t('charges.list.kilometers') as string,
                orderColumn: 'c.kilometers',
                width: 120,
                hideOn: ['sm', 'md'],
                headerNote: `${t('common.unit')}: ${t('charges.list.kilometers_minified')}`,
                renderCell: row => row.kilometers != null && row.kilometers != undefined ? (
                    <IconWithText
                        icon={GaugeIcon}
                        value={row.kilometers}
                        suffix={t('charges.list.kilometers_minified')}
                        isNumeric={true}
                    />
                ) : <>-</>,
            },
        ] : [];

        const kmSinceFuelingColumns: DataTableColumn<ChargeDto>[] = (!isTabView || showField(SEGMENT_FEATURES.FUEL)) ? [
            {
                field: 'kmSinceLastFueling' as keyof ChargeDto,
                name: t('charges.list.km_since_last_fueling') as string,
                orderColumn: 'km_since_last_fueling',
                width: 150,
                hideOn: ['sm', 'md'],
                headerNote: `${t('common.unit')}: ${t('charges.list.kilometers_minified')}`,
                exportValue: row => (row.typeName === GenericType.FUEL && row.kmSinceLastFueling != null && row.kmSinceLastFueling != undefined) ? `${row.kmSinceLastFueling}` : '',
                renderCell: row => row.typeName === GenericType.FUEL && row.kmSinceLastFueling != null && row.kmSinceLastFueling != undefined ? (
                    <IconWithText
                        icon={GaugeIcon}
                        value={row.kmSinceLastFueling}
                        suffix={t('charges.list.kilometers_minified')}
                        isNumeric={true}
                    />
                ) : <>-</>,
            },
        ] : [];

        const hoursColumns: DataTableColumn<ChargeDto>[] = (!isTabView || showField(SEGMENT_FEATURES.HOURS)) ? [
            {
                field: 'hours' as keyof ChargeDto,
                name: t('common.hours') as string,
                orderColumn: 'c.hours',
                width: 110,
                hideOn: ['sm', 'md'],
                headerNote: `${t('common.unit')}: ${t('common.hours_symbol')}`,
                renderCell: row => row.hours != null && row.hours != undefined ? (
                    <IconWithText
                        icon={ClockIcon}
                        value={row.hours}
                        suffix={t('common.hours_symbol')}
                        isNumeric={true}
                    />
                ) : <>-</>,
            },
        ] : [];

        const litersColumns: DataTableColumn<ChargeDto>[] = (!isTabView || showField(SEGMENT_FEATURES.FUEL)) ? [
            {
                field: 'liters' as keyof ChargeDto,
                name: <><span data-tip={t('charges.list.liters_kwh_tooltip' as any)}>{t('charges.list.liters_kwh' as any)}</span><ReactTooltip /></>,
                orderColumn: 'c.liters',
                width: 130,
                hideOn: ['sm', 'md'],
                // Task #66220: no Excel a célula fica numérica (para dar para fazer contas) e a unidade
                // vive no cabeçalho, já que varia por linha (litros ou kWh).
                exportName: `${t('charges.list.liters_kwh')} (${t('common.lt_symbol')} | ${t('common.kwh')})`,
                exportValue: row => {
                    if (row.typeName !== GenericType.FUEL || row.liters == null || row.liters == undefined || row.liters <= 0) return '';
                    return row.liters;
                },
                renderCell: row => row.typeName === GenericType.FUEL && row.liters != null && row.liters != undefined && row.liters > 0 ? (
                    <div className={styles.costColumn}>
                        <MoneyFormat value={row.liters} suffix={row.fuelName === FUELS.ELECTRIC ? t('common.kwh') as string : t('common.lt') as string} fixedDecimalScale={true} />
                    </div>
                ) : <>-</>,
            },
        ] : [];

        const pricePerLiterColumns: DataTableColumn<ChargeDto>[] = (!isTabView || showField(SEGMENT_FEATURES.FUEL)) ? [
            {
                field: 'pricePerLiter' as keyof ChargeDto,
                name: <><span data-tip={t('charges.list.price_per_liter_tooltip' as any)}>{t('charges.list.price_per_liter' as any)}</span><ReactTooltip /></>,
                orderColumn: 'price_per_liter',
                width: 120,
                hideOn: ['sm', 'md'],
                // Task #66220: célula numérica arredondada a 3 casas; a unidade (€/L ou €/kWh) vai no cabeçalho.
                exportName: `${t('charges.list.price_per_liter')} (${t('charges.list.price_per_liter_unit')} | ${t('charges.list.price_per_kwh_unit')})`,
                exportValue: row => {
                    if (row.typeName !== GenericType.FUEL || row.pricePerLiter == null || row.pricePerLiter == undefined) return '';
                    return Math.round(row.pricePerLiter * 1000) / 1000;
                },
                renderCell: row => row.typeName === GenericType.FUEL && row.pricePerLiter != null && row.pricePerLiter != undefined ? (
                    <div className={styles.costColumn}>
                        <MoneyFormat value={row.pricePerLiter} decimalScale={3} fixedDecimalScale={true} suffix={row.fuelName === FUELS.ELECTRIC ? t('charges.list.price_per_kwh_unit') as string : t('charges.list.price_per_liter_unit') as string} />
                    </div>
                ) : <>-</>,
            },
        ] : [];

        const cols: DataTableColumn<ChargeDto>[] = [
            ...vehicleColumns,
            {
                field: 'responsibleName' as keyof ChargeDto,
                name: t('common.responsible') as string,
                orderColumn: 'responsible.real_name',
                width: 180,
                hideOn: ['sm'],
                exportValue: row => row.responsibleName ?? '',
                renderCell: row => (
                    <AvatarWithText src={row.responsiblePhotoUrl}
                        size="x2"
                        hideAvatar={!row.responsibleName}><div>{row.responsibleName || '-'}</div></AvatarWithText>
                ),
            },
            {
                field: 'date' as keyof ChargeDto,
                name: t('charges.charge_date') as string,
                orderColumn: 'c.date',
                width: 140,
                hideOn: ['sm'],
                exportValue: row => row.date ? moment(row.date).format(DATE_FORMAT_DEFAUT) : '',
                renderCell: row => <DateFormat value={row.date} />,
            },
            {
                field: 'typeName' as keyof ChargeDto,
                name: t('charges.list.type') as string,
                orderColumn: 'translated_type_name',
                width: 150,
                hideOn: ['sm', 'md'],
                exportValue: row => {
                    if (row.typeName === GenericType.FUEL && row.fuelName) return t('vehicle.fuels.' + row.fuelName as any) as string;
                    return !row.typeIsSystem ? (row.typeName ?? '') : t('common.generic_types.' + row.typeName as any) as string;
                },
                renderCell: row => (
                    <div className={styles.cellContainer}>
                        <div className={styles.rowFlex}>
                            {row.typeIconSvg && <RenderSVG item={row.typeIconSvg} className={styles.divSVG} />}
                            {(row.typeName != GenericType.FUEL || !row.fuelName) && <span>{!row.typeIsSystem ? row.typeName : t('common.generic_types.' + row.typeName as any)}</span>}
                            {row.typeName == GenericType.FUEL && row.fuelName && <span>{t('vehicle.fuels.' + row.fuelName as any)}</span>}
                        </div>
                    </div>
                ),
            },
            ...kmColumns,
            ...kmSinceFuelingColumns,
            ...hoursColumns,
            {
                field: 'supplierName' as keyof ChargeDto,
                name: t('common.supplier') as string,
                orderColumn: 'supplier.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg', 'xl'],
                exportValue: row => row.supplierName ?? '',
                renderCell: row => <>
                    <div data-tip={row.supplierUrl ? (row.supplierName || '-') : undefined}>
                        {row.supplierUrl && <img src={row.supplierUrl} style={{ height: '2.5rem' }}></img>}
                        {!row.supplierUrl && (row.supplierName || '-')}
                    </div>
                    <ReactTooltip />
                </>,
            },
            {
                field: 'cost' as keyof ChargeDto,
                name: t('common.cost') as string,
                orderColumn: 'c.cost',
                width: 120,
                headerNote: `${t('common.unit')}: ${t('common.euro')}`,
                renderCell: row => (
                    <div className={styles.costColumn}>
                        <MoneyFormat fixedDecimalScale={true} value={row.cost} />
                    </div>
                ),
            },
            ...litersColumns,
            ...pricePerLiterColumns,
            {
                field: 'paymentDate' as keyof ChargeDto,
                name: t('charges.payment_date') as string,
                orderColumn: 'c.payment_date',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.paymentDate ? moment(row.paymentDate).format(DATE_FORMAT_DEFAUT) : '',
                renderCell: row => row.paymentDate ? <DateFormat value={row.paymentDate} format={DATE_FORMAT_DEFAUT} /> : <>-</>,
            },
            {
                field: 'geofencesPercentage' as keyof ChargeDto,
                name: t('charges.list.cost_distribution') as string,
                orderColumn: 'geof.geofences_percentage',
                width: 120,
                hideOn: ['sm', 'md', 'lg', 'xl'],
                headerNote: `${t('common.unit')}: %`,
                renderCell: row => (
                    <div>
                        {hasReadCostCenterPolicy && row.geofencesPercentage >= 100 ? <CostCenterCompleteIcon data-tip={t('charges.list.geofence_percentage_complete_tooltip')} /> : <></>}
                        {(hasReadCostCenterPolicy && row.geofencesPercentage < 100 && row.geofencesPercentage > 0) ? <CostCenterIncompleteIcon data-tip={t('charges.list.geofence_percentage_incomplete_tooltip')} /> : <></>}
                        {hasReadCostCenterPolicy && row.geofencesPercentage <= 0 ? <CostCenterEmptyIcon data-tip={t('charges.list.geofence_percentage_empty_tooltip')} /> : <></>}
                        <ReactTooltip />
                    </div>
                ),
            },
            {
                field: 'requestRefund' as keyof ChargeDto,
                name: t('charges.refund') as string,
                hideOrderButton: true,
                width: 180,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => {
                    if (!row.requestRefund) return '-';
                    const parts: string[] = [];
                    if (row.refundHistory && row.refundHistory[0]?.status && row.refundHistory[0]?.situation !== SituationType.TREATED) {
                        let statusText = t(('charges.list.request_status_type.' + row.refundHistory[0].status) as any) as string;
                        if (row.refundHistory[0].createdDate) {
                            statusText += ' - ' + moment(row.refundHistory[0].createdDate).format(DATE_FORMAT_DEFAUT);
                        }
                        parts.push(statusText);
                    }
                    if (row.refundHistory && row.refundHistory[0]?.situation) {
                        let situationText = t(('charges.situations.' + row.refundHistory[0].situation) as any) as string;
                        if (row.refundHistory[0].dateRefundTreated) {
                            situationText += ' - ' + moment(row.refundHistory[0].dateRefundTreated).format(DATE_FORMAT_DEFAUT);
                        }
                        parts.push(situationText);
                    }
                    if (!row.refundHistory) {
                        parts.push(t('charges.waiting_approval') as string);
                    }
                    return parts.join(' | ') || '-';
                },
                renderCell: row => <>
                    {row.requestRefund ?
                        <>
                            {row.refundHistory && row.refundHistory[0].status && row.refundHistory[0].situation != SituationType.TREATED &&
                                <div className={styles.refundTreatedArea} style={{ color: (ChargesStatusColorEnum as any)[row.refundHistory[0].status] }}>
                                    <span>
                                        {t(('charges.list.request_status_type.' + row.refundHistory[0].status) as any)}
                                        {row.refundHistory[0].createdDate &&
                                            <>
                                                {' - '}
                                                <DateFormat value={row.refundHistory[0].createdDate} />
                                            </>
                                        }
                                    </span>
                                </div>}
                            {row.refundHistory && row.refundHistory[0].situation &&
                                <div className={styles.refundTreatedArea} style={{ color: (ChargesStatusColorEnum as any)[row.refundHistory[0].situation] }}>
                                    <span>
                                        {t(('charges.situations.' + row.refundHistory[0].situation) as any)}
                                        {row.refundHistory[0].dateRefundTreated &&
                                            <>
                                                {' - '}
                                                <DateFormat value={row.refundHistory[0].dateRefundTreated} />
                                            </>}
                                    </span>
                                </div>
                            }
                            {!row.refundHistory &&
                                <div className={styles.refundTreatedArea} style={{ color: ChargesStatusColorEnum.WAITING_APPROVAL }}>
                                    <span>{t('charges.waiting_approval')} </span>
                                </div>
                            }
                        </>
                        :
                        <div>
                            <span>{'-'}</span>
                        </div>
                    }
                </>,
            },
        ];

        if (showAiSelected) {
            cols.push({
                field: 'id' as keyof ChargeDto,
                name: <><div data-tip={t('common.select_all')}>
                    <SimpleToggleSwitch
                        checked={isSelectAllChecked}
                        onChange={(val: any) => onSelectAll(val)}
                    /></div>
                    <ReactTooltip /></>,
                width: 60,
                hideOrderButton: true,
                preventClick: true,
                renderCell: row =>
                    <SimpleToggleSwitch
                        checked={aiSelected.map(x => x.id ?? '').includes(row.id ?? '')}
                        onChange={() => {
                            if (isSelectAllChecked) { setIsSelectAllChecked(false) }
                            setAiSelected(s => s.map(x => x.id ?? '').includes(row.id ?? '') ? s.filter(x => (x.id ?? '') != (row.id ?? '')) : [...s, row]);
                        }}
                    />
            });
        }

        return cols;
    }, [aiSelected, showAiSelected, vehicleSegmentFeatures, t, hasReadCostCenterPolicy, isVehicle, isDriver]);


    const navigateTo = (type?: string, id?: string) => {
        if (id) {
            history.push(`/charges/${type}/${id}`);

        } else {
            history.push(`/charges/${type}`);
        }
    }

    const JumpToImport = () => {
        history.push(`/import-charges`);
    }

    const onRowClickStandalone = (row: ChargeDto, index: number, event: any) => {
        if (!row.id) return;
        event.preventDefault();
        event.stopPropagation();

        const actualPage = calculatePageFromIndex(index);
        const newFilters = { ...filters, scrollToItemId: row.id, page: actualPage };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        newFilters.allIn = criteria.allIn;
        saveFiltersOnCache(newFilters);

        if (event.ctrlKey) {
            window.open('charges/details/' + row.id, '_blank');
            return;
        }
        navigateTo('details', row.id);
    }

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        // Don't save page here - only save it when navigating to details (via onRowClickStandalone)
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        newFilters.allIn = criteria.allIn;
        saveFiltersOnCache(newFilters);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            setShowRemoveModal(false);
            Loading.show();
            await ChargesService.remove(itemToRemove);
            skipCounters.current = false;

            setCharges([]);
            resetScrollState();
            setTotalItems(0);

            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
            Loading.hide();
            if (onDeleteFromList) {
                onDeleteFromList();
            }
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete charge`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            Loading.hide();
        }
    };

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && charges.length > 0;
            getData(isLoadMore);
        }
    }, [criteria]);

    useEffect(() => {
        (async () => {
            if (hasReadCostCenterPolicy) {
                const geofences = await GeofencesService.catalog();
                setGeofencesOptions(geofences);
            }

        })();
        return;
    }, []);

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }))
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const loadFilters = () => {
        isFirstLoad.current = false;

        const cachedFilters = localStorage.getItem(filterJsonKey);

        // When the parent screen owns the filters (criteriaProp), the cache only
        // holds ordering state — applying it as filters would overwrite the
        // criteria the parent just selected.
        if (criteriaProp) {
            if (cachedFilters != null) {
                const f: Filters = JSON.parse(cachedFilters) as Filters;
                if (f.orderBy && f.orderColumn) {
                    setCriteria(prev => ({ ...prev, orderBy: f.orderBy, orderColumn: f.orderColumn }));
                    setOrder({
                        field: getFieldFromOrderColumn(f.orderColumn),
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                    return;
                }
            }
            getData();
            return;
        }

        if (cachedFilters != null && !month) {

            const f: Filters = JSON.parse(cachedFilters) as Filters;

            if (isDriver) {
                f.responsibleId = driverId;
                f.vehicleId = undefined;
            }

            if (isVehicle) {
                f.vehicleId = vehicleId;
            }

            if (month) {
                f.startDate = moment(month).startOf('month').toDate();
                f.endDate = moment(month).endOf('month').toDate();
            }

            // Handle scroll restoration for standalone view
            if (isStandaloneView && f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(filterJsonKey, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    startDate: f.startDate,
                    endDate: f.endDate,
                    vehicleId: isVehicle ? vehicleId : f.vehicleId,
                    typeId: f.typeId,
                    fuelId: f.fuelId,
                    supplierId: f.supplierId,
                    responsibleId: f.responsibleId,
                    minCost: f.cost && f.cost[0] != undefined ? f.cost[0] : undefined,
                    maxCost: f.cost && f.cost[1] != undefined ? f.cost[1] : undefined,
                    refundSituation: f.refundSituation,
                    refundState: f.refundState,
                    vehiclesInactive: f.vehiclesInactive,
                    geofenceId: f.geofenceId,
                    hasGeofence: f.hasGeofence,
                    localIds: f.localIds,
                    localOperator: f.localOperator,
                    segmentId: f.segmentId,
                    page: startPage,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    allIn: f.allIn
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: getFieldFromOrderColumn(f.orderColumn),
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }

                return;
            }

            onChangeFilters(f, false, true)

        }
        else { getData() }
    }

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        skipCounters.current = false;

        setCharges([]);
        resetScrollState();
        setTotalItems(0);

        setCriteria(prev => ({ ...prev, page: 1, allIn: value }));
    }, 500);

    const onChangeFilters = (f: Filters, skipCountersProp: boolean, isOnLoad?: boolean) => {
        // Set skipCounters FIRST so the useEffect[criteria] triggered by setCriteria
        // below sees the correct value, consistent with handleOrderChange / debounced.
        skipCounters.current = skipCountersProp;

        updateTotalFilters(f);

        saveFiltersOnCache(f);
        setReRenderSearch(r => r + 1);

        if (!isOnLoad) {
            setCharges([]);
            resetScrollState();
            setTotalItems(0);
        }

        setCriteria(prev => ({
            ...prev,
            startDate: f.startDate,
            endDate: f.endDate,
            vehicleId: isVehicle ? vehicleId : f.vehicleId,
            typeId: f.typeId,
            fuelId: f.fuelId,
            supplierId: f.supplierId,
            responsibleId: f.responsibleId,
            minCost: f.cost && f.cost[0] != undefined ? f.cost[0] : undefined,
            maxCost: f.cost && f.cost[1] != undefined ? f.cost[1] : undefined,
            refundSituation: f.refundSituation,
            refundState: f.refundState,
            vehiclesInactive: f.vehiclesInactive,
            geofenceId: f.geofenceId,
            hasGeofence: f.hasGeofence,
            localIds: f.localIds,
            localOperator: f.localOperator,
            segmentId: f.segmentId,
            page: 1, // Always start from page 1 - scroll restoration handles page > 1 separately
            orderBy: f.orderBy || prev.orderBy || 'desc',
            orderColumn: f.orderColumn || prev.orderColumn || 'c.date',
            allIn: f.allIn
        }))

        setOrder({
            field: getFieldFromOrderColumn(f.orderColumn || 'c.date'),
            orderColumn: f.orderColumn || 'c.date',
            isOrderAsc: (f.orderBy || 'desc') === 'asc',
        });
    }

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.startDate || f.endDate) {
            total++;
        }

        if (f.vehicleId && f.vehicleId.length > 0 && !isVehicle) {
            total++;
        }

        if (f.typeId && f.typeId.length > 0) {
            total++;
        }

        if (f.fuelId && f.fuelId.length > 0) {
            total++;
        }

        if (f.supplierId && f.supplierId.length > 0) {
            total++;
        }

        if (f.responsibleId && f.responsibleId.length > 0 && !isDriver) {
            total++;
        }

        if (f.refundSituation != null) {
            total++;
        }

        if (f.refundState) {
            total++;
        }
        if (f.cost && (f.cost[0] != undefined || f.cost[1] != undefined)) {
            total++;
        }
        if (f.vehiclesInactive && !isVehicle) {
            total++;
        }

        if (f.geofenceId) {
            total++;
        }

        if (f.hasGeofence != null) {
            total++;
        }

        if (f.segmentId) {
            total++;
        }

        if (f.localIds?.length) {
            total++;
        }

        setFiltersTotal(total);
    }

    const updateFilters = (items: { key: string; value: any }[]) => {
        const newFilters = JSON.parse(JSON.stringify(filters));

        items.forEach((item: { key: string; value: any }) => {
            newFilters[item.key] = item.value
        })

        newFilters.orderBy = filters.orderBy ?? criteria.orderBy;
        newFilters.orderColumn = filters.orderColumn ?? criteria.orderColumn;
        newFilters.allIn = filters.allIn ?? criteria.allIn;
        onChangeFilters(newFilters, false)
    }

    // FilterBadgesOverflow items (standalone view)
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('vehicle.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => updateFilters([{ key: 'vehicleId', value: undefined }, { key: 'vehicleName', value: undefined }])
            });
        }

        if (filters.typeName) {
            items.push({
                key: 'type',
                label: t('common.type') as string,
                value: filters.typeName,
                onRemove: () => updateFilters([{ key: 'typeId', value: undefined }, { key: 'typeName', value: undefined }])
            });
        }

        if (filters.fuelName) {
            items.push({
                key: 'fuel',
                label: t('vehicle.fuel') as string,
                value: filters.fuelName,
                onRemove: () => updateFilters([{ key: 'fuelId', value: undefined }, { key: 'fuelName', value: undefined }])
            });
        }

        if (filters.refundState != null) {
            items.push({
                key: 'refundState',
                label: t('charges.reimbursement_status') as string,
                value: t(('charges.list.request_status_type.' + filters.refundState) as any) as string,
                onRemove: () => updateFilters([{ key: 'refundState', value: undefined }])
            });
        }

        if (filters.refundSituation != null) {
            items.push({
                key: 'refundSituation',
                label: t('charges.reimbursement_situation') as string,
                value: t(('charges.situations.' + filters.refundSituation) as any) as string,
                onRemove: () => updateFilters([{ key: 'refundSituation', value: undefined }])
            });
        }

        if (filters.responsibleName) {
            items.push({
                key: 'responsible',
                label: t('common.responsible') as string,
                value: filters.responsibleName,
                onRemove: () => updateFilters([{ key: 'responsibleId', value: undefined }, { key: 'responsibleName', value: undefined }])
            });
        }

        if (filters.supplierName) {
            items.push({
                key: 'supplier',
                label: t('common.supplier') as string,
                value: filters.supplierName,
                onRemove: () => updateFilters([{ key: 'supplierId', value: undefined }, { key: 'supplierName', value: undefined }])
            });
        }

        if (filters.cost?.filter(x => x !== undefined).length) {
            let value = '';
            if (filters.cost.filter(i => i !== undefined && i !== null).length === 2) {
                value = `${filters.cost[0]} - ${filters.cost[1]}`;
            } else if (filters.cost[0] !== undefined && filters.cost[0] !== null) {
                value = `> ${filters.cost[0]}`;
            } else {
                value = `< ${filters.cost[1]}`;
            }
            items.push({
                key: 'cost',
                label: t('common.cost') as string,
                value,
                onRemove: () => updateFilters([{ key: 'cost', value: undefined }])
            });
        }

        if (filters.startDate || filters.endDate) {
            let value = '';
            if (filters.startDate && filters.endDate) {
                value = `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`;
            } else if (filters.startDate) {
                value = `${t('common.range.from')} ${new Date(filters.startDate).toLocaleDateString()}`;
            } else if (filters.endDate) {
                value = `${t('common.range.to')} ${new Date(filters.endDate).toLocaleDateString()}`;
            }
            items.push({
                key: 'dates',
                label: t('charges.filter_date') as string,
                value,
                onRemove: () => updateFilters([{ key: 'startDate', value: undefined }, { key: 'endDate', value: undefined }])
            });
        }

        if (filters.vehiclesInactive) {
            items.push({
                key: 'inactive',
                label: t('charges.vehicle_inactives') as string,
                value: t('common.yes') as string,
                onRemove: () => updateFilters([{ key: 'vehiclesInactive', value: undefined }])
            });
        }

        if (filters.geofenceName) {
            items.push({
                key: 'geofence',
                label: t('charges.geofences.title') as string,
                value: filters.geofenceName,
                onRemove: () => updateFilters([{ key: 'geofenceId', value: undefined }, { key: 'geofenceName', value: undefined }])
            });
        }

        if (filters.hasGeofence != undefined) {
            items.push({
                key: 'hasGeofence',
                label: t('charges.has_geofence') as string,
                value: (filters.hasGeofence ? t('common.yes') : t('common.no')) as string,
                onRemove: () => updateFilters([{ key: 'hasGeofence', value: undefined }])
            });
        }

        if (filters.segmentName) {
            items.push({
                key: 'segment',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => updateFilters([{ key: 'segmentId', value: undefined }, { key: 'segmentName', value: undefined }])
            });
        }

        if (filters.localNames?.length) {
            items.push({
                key: 'local',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => updateFilters([{ key: 'localIds', value: [] }, { key: 'localNames', value: [] }])
            });
        }

        return items;
    }, [filters, t]);

    const renderCounterBoxes = () => (
        <div className={compactPageLayoutStyles.rowCounters}>
            {counters && <InfoCard
                label={t('charges.list.average_cost_per_month') as string}
                value={counters.averageCostPerMonth ?? 0}
                icon={MediaIcon}
                backgroundColor={INFO_CARD_COLORS[0]}
                isMoney
            />}
            {counters && <InfoCard
                label={t('charges.list.average_cost_of_charge_per_car') as string}
                value={counters.averageCostChargePerCar ?? 0}
                icon={CarIcon}
                backgroundColor={INFO_CARD_COLORS[1]}
                isMoney
            />}
            {counters && <InfoCard
                label={t('charges.list.cumulative_total_cost') as string}
                value={counters.cumulativeTotalCost ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[2]}
                isMoney
            />}
        </div>
    );

    const renderCounterBoxForTab = () => (
        <div className={compactPageLayoutStyles.rowCounters}>
            {counters && <InfoCard
                label={t('charges.list.average_cost_per_month') as string}
                value={counters.averageCostPerMonth ?? 0}
                icon={MediaIcon}
                backgroundColor={INFO_CARD_COLORS[0]}
                isMoney
            />}
            {counters && <InfoCard
                label={t('charges.list.cumulative_total_cost') as string}
                value={counters.cumulativeTotalCost ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[1]}
                isMoney
            />}
        </div>
    );

    const onOpenAIPage = async (type: string) => {
        if (isSelectAllChecked && aiSelected.length !== totalItems) {
            history.push(`/charge-geofenses`, { calculation: type, geofenceList: geofencesOptions, selectedCharges: [], listCriteria: criteria });
        } else {
            history.push(`/charge-geofenses`, { calculation: type, geofenceList: geofencesOptions, selectedCharges: aiSelected });
        }
    };

    const fetchAllForExport = useCallback(async () => {
        const result = await ChargesService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    return (
        <ScreenTitle title={t('charges.title')} {...props}>

            {/* === TAB VIEW (isVehicle || isDriver) === */}
            {(isVehicle || isDriver) ?
                <div className={styles.listWrapper}>
                    <div className={styles.tabContentSection}>
                        <div className={styles.tabHeader}>
                            <div>
                                <InputSearch inputContainerClassName={styles.inputContainer} defaultValue={criteria.allIn} onChangeValue={debounced} placeholder={showField(SEGMENT_FEATURES.KM) ? t('charges.list.search_with_km') : t('charges.list.search')} />
                            </div>
                            <div className={styles.rowFlex}>
                                {!criteriaProp && <Popover
                                    contentContainerClassName={styles.filtersPopoverContainer}
                                    containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                                    positions={['left']}
                                    onClickOutside={() => updateTotalFilters(filters)}
                                    content={setIsPopoverOpen => <ChargeFiltersScreen
                                        isVehicle={isVehicle}
                                        isDriver={isDriver}
                                        filters={filters}
                                        onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f, false) }}
                                        onChange={f => updateTotalFilters(f)}
                                    />}>
                                    {(isPopoverOpen, setIsPopoverOpen) => (
                                        <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                            {filtersTotal > 0 &&
                                                <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                            }
                                        </ScreenHeaderButton>
                                    )}
                                </Popover>}
                                <ExportListingButton exportRef={exportListingRef} />
                                {createNewCharge && editable && hasChargesWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={createNewCharge} />}
                            </div>
                        </div>
                        {counters && renderCounterBoxForTab()}
                    </div>
                    <div className={styles.listTblWrapper}>
                        {isFirstLoad.current === false && (
                            <DataTableWithSidebar
                                key={reRenderSearch.toString()}
                                cacheKey="CHARGES_VEHICLE_TABLE"
                                columns={dataTableColumns}
                                defaultHiddenColumns={['vehicleSegmentName', 'vehicleLocalNames']}
                                rows={charges}
                                defaultOrder={order}
                                onOrderChange={handleOrderChange}
                                onRowClick={row => onClickItem(row, true)}
                                onRenderAction={renderTableActionCell}
                                onRowId={row => row.id ?? ''}
                                infiniteScroll={{
                                    isLoading: isLoadingMore,
                                    hasMore: getHasMore(charges.length, totalItems),
                                    onLoadMore: getOnLoadMore(loadMore),
                                    hasMorePrevious: getHasMorePrevious(),
                                    isLoadingPrevious,
                                    onLoadPrevious: getOnLoadPrevious(loadPrevious),
                                }}
                                exportExcel={{
                                    fetchAll: fetchAllForExport,
                                    fileName: t('charges.title') as string,
                                }}
                                exportRef={exportListingRef}
                                hideExportInSidebar={true}
                            />
                        )}
                    </div>
                    <QuestionYesNo
                        onNo={onCancelRemove}
                        onYes={onRemove}
                        isVisible={showRemoveModal}
                        message={t('common.messages.remove_charge')} />
                </div> : <></>}

            {/* === STANDALONE VIEW (!isVehicle && !isDriver) — refactored === */}
            {isStandaloneView ?
                <CompactPageLayout
                    title={t('charges.title') as string}
                    contentAreaClassName={styles.contentArea}
                    middleContent={<FilterBadgesOverflow items={filterItems} align="right" />}
                    headerButtons={
                        <>
                            <Popover
                                contentContainerClassName={styles.filtersPopoverContainer}
                                containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                                positions={['bottom', 'left']}
                                align={'end'}
                                onClickOutside={() => updateTotalFilters(filters)}
                                content={setIsPopoverOpen => <ChargeFiltersScreen
                                    isVehicle={isVehicle}
                                    isDriver={isDriver}
                                    filters={filters}
                                    onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f, false) }}
                                    onChange={f => updateTotalFilters(f)}
                                />}>
                                {(isPopoverOpen, setIsPopoverOpen) => (
                                    <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                        {filtersTotal > 0 &&
                                            <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                        }
                                    </ScreenHeaderButton>
                                )}
                            </Popover>
                            <ExportListingButton exportRef={exportListingRef} />
                            {hasImportChargesWritePolicy && <div data-tip={t('import_charges.tooltip')}><ScreenHeaderButton className={styles.emptyBorder} classNameIcon={styles.importIcon} icon={ImportIcon} onClick={() => JumpToImport()} /></div>}
                            {hasChargesWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
                            {hasReadCostCenterPolicy && <Dropdown style={{ right: '10px' }} options={
                                <>
                                    {!showAiSelected && <DropdownItem onClick={() => setShowAiSelected(true)}>
                                        {t('charges.list.select_charges')}
                                    </DropdownItem>}
                                    {showAiSelected && <DropdownItem onClick={() => { setShowAiSelected(false); setAiSelected([]); }}>
                                        {t('charges.list.unselect_charges')}
                                    </DropdownItem>}
                                    {aiSelected.length > 0 && <><DropdownItem onClick={() => {
                                        onOpenAIPage('Km');
                                    }}>
                                        {t('charges.list.link_geofences_km')}
                                    </DropdownItem>
                                        <DropdownItem onClick={() => {
                                            onOpenAIPage('Duration');
                                        }}>
                                            {t('charges.list.link_geofences_duration')}
                                        </DropdownItem>
                                    </>
                                    }
                                </>
                            }>
                                <div>
                                    <ScreenHeaderButton icon={AiIcon} className={styles.forceNoMarginLeft} />
                                </div>
                            </Dropdown>}
                            <ReactTooltip />
                        </>
                    }
                >
                    {counters && (
                        <div className={compactPageLayoutStyles.countersSection}>
                            <VatNotice />
                            {renderCounterBoxes()}
                        </div>
                    )}

                    <div className={compactPageLayoutStyles.listTblWrapper}>
                        {isFirstLoad.current === false && (
                            <DataTableWithSidebar
                                key={reRenderSearch.toString()}
                                cacheKey="CHARGES_TABLE"
                                columns={dataTableColumns}
                                defaultHiddenColumns={['vehicleSegmentName', 'vehicleLocalNames']}
                                rows={charges}
                                defaultOrder={order}
                                onOrderChange={handleOrderChange}
                                onRowClick={(row, index, event) => onRowClickStandalone(row, index, event)}
                                onHref={row => `charges/details/${row.id}`}
                                onRenderAction={renderTableActionCell}
                                onRowId={row => row.id ?? ''}
                                scrollToRowId={scrollToItemId}
                                infiniteScroll={{
                                    isLoading: isLoadingMore,
                                    hasMore: getHasMore(charges.length, totalItems),
                                    onLoadMore: getOnLoadMore(loadMore),
                                    hasMorePrevious: getHasMorePrevious(),
                                    isLoadingPrevious: isLoadingPrevious,
                                    onLoadPrevious: getOnLoadPrevious(loadPrevious),
                                }}
                                exportExcel={{
                                    fetchAll: fetchAllForExport,
                                    fileName: t('charges.title') as string,
                                }}
                                exportRef={exportListingRef}
                                hideExportInSidebar={true}
                            />
                        )}
                    </div>

                    <QuestionYesNo
                        onNo={onCancelRemove}
                        onYes={onRemove}
                        isVisible={showRemoveModal}
                        message={t('common.messages.remove_charge')} />
                </CompactPageLayout> : <></>}

            <ReactTooltip />
        </ScreenTitle>
    );
}

export default ListChargesScreen;
